import * as idbKeyval from 'idb-keyval';
import { z } from 'zod';

import { trackError } from 'backend-client-web';
import { PersistableClientCore } from 'persistable-types';
import { toArrayBufferBacked, wrapSymAesEncryptionKey } from 'vault-crypto';

export type IdbKeyvalEntry<S> = {
  readonly key: string;
  readonly parse: (value: unknown) => S;
};

/**
 * @deprecated Only used to decrypt the legacy format of encryptedClientCoreParameters.
 */
export interface LegacyCCPEncryptionInfo {
  symEncryptionKey: CryptoKey;
  initializationVector: Uint8Array<ArrayBuffer>;
}

const legacyCCPEncryptionInfoSchema = z
  .object({
    symEncryptionKey: z.instanceof(window.CryptoKey),
    initializationVector: z.instanceof(Uint8Array),
  })
  // Adding new keys to the schema is probably an incompatible change so we might as well fail early
  .strict();

const ccpEncryptionKeySchema = z.instanceof(window.CryptoKey).transform(wrapSymAesEncryptionKey);

export const IDB_KEYS = Object.freeze({
  /**
   * @deprecated Only used to decrypt the legacy format of encryptedClientCoreParameters.
   */
  clientCoreParametersEncryptionInfo: {
    key: 'clientCoreParametersEncryptionInfo',
    parse: (data: unknown) => legacyCCPEncryptionInfoSchema.parse(data),
  },
  clientCoreParametersEncryptionKey: {
    key: 'clientCoreParametersEncryptionKey',
    parse: (data: unknown) => ccpEncryptionKeySchema.parse(data),
  },
  encryptedPersistableClientCore: {
    key: 'encryptedPersistableClientCore',
    parse: parseAsUint8Array,
  },
  /** @deprecated Now stored as encryptedPersistableClientCore, use only for migration. */
  persistableClientCore: {
    key: 'persistableClientCore',
    parse: (data: unknown) => PersistableClientCore.parse(data),
  },
});

export async function getIdbKeyvalEntry<S>({
  key,
  parse,
}: IdbKeyvalEntry<S>): Promise<S | undefined> {
  const entry = await idbKeyval.get(key);
  if (!entry) {
    return undefined;
  }
  try {
    return parse(entry);
  } catch (e) {
    trackError(new Error(`Error reading from IdbKeyval (key ${key})`, { cause: e }));
    return undefined;
  }
}

export async function setIdbKeyvalEntry<S>({ key }: IdbKeyvalEntry<S>, newValue: S | undefined) {
  if (newValue === undefined) {
    await idbKeyval.del(key);
  } else {
    await idbKeyval.set(key, newValue);
  }
}

function parseAsUint8Array(arr: unknown): Uint8Array<ArrayBuffer> {
  if (arr instanceof Uint8Array) {
    return toArrayBufferBacked(arr);
  }
  throw new TypeError('Expected uint8Array');
}
